import { FileText, MapPin } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  fetchInboundMediaObjectUrl,
  getWhatsAppAttachmentViewUrl,
  isDoubleTickMediaUrl,
} from './whatsapp.api';

/**
 * WhatsApp-style inline media rendering for a chat bubble.
 *
 * Inbound media carries a public provider URL and renders directly.
 * OUTBOUND attachments store `s3://<key>` (private bucket); those are
 * swapped for a CDN URL once via the view-url endpoint - resolved URLs are
 * memoised module-wide so re-renders and re-opened threads don't refetch.
 */

const resolvedUrls = new Map<string, string>();

/**
 * Message mediaUrl -> something the browser can actually load:
 *   - `s3://<key>` (our outbound uploads)     -> CDN URL via view-url
 *   - DoubleTick-hosted (inbound customer media, 403s without the account
 *     API key) -> object URL via the API's authenticated media proxy
 *   - anything else                           -> used as-is
 * Resolutions are memoised module-wide so scrolling never refetches.
 */
const useMediaUrl = (mediaUrl: string): string | null => {
  const needsResolve = mediaUrl.startsWith('s3://') || isDoubleTickMediaUrl(mediaUrl);
  const direct = needsResolve ? null : mediaUrl;
  const [resolved, setResolved] = useState<string | null>(
    direct ?? resolvedUrls.get(mediaUrl) ?? null,
  );

  useEffect(() => {
    if (direct || resolvedUrls.has(mediaUrl)) return;
    let cancelled = false;
    const resolve = mediaUrl.startsWith('s3://')
      ? getWhatsAppAttachmentViewUrl(mediaUrl)
      : fetchInboundMediaObjectUrl(mediaUrl);
    resolve
      .then((url) => {
        resolvedUrls.set(mediaUrl, url);
        if (!cancelled) setResolved(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [mediaUrl, direct]);

  return direct ?? resolved;
};

/** Coordinates out of the stored maps link (`https://maps.google.com/?q=lat,lng`). */
const coordsFromMapsLink = (url: string): { lat: number; lng: number } | null => {
  const m = /[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(url);
  return m ? { lat: Number(m[1]), lng: Number(m[2]) } : null;
};

const MediaBubble = ({
  mediaUrl,
  kind,
  body,
}: {
  mediaUrl: string;
  kind: string;
  body: string | null;
}): React.JSX.Element => {
  const url = useMediaUrl(mediaUrl);
  const [broken, setBroken] = useState(false);

  // Location: pin card that opens the maps link (mediaUrl IS the maps link).
  if (kind === 'LOCATION') {
    const coords = coordsFromMapsLink(mediaUrl);
    return (
      <a
        href={mediaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mb-1 flex items-center gap-2 rounded-md bg-black/5 p-2 no-underline hover:bg-black/10"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#128C7E] text-white">
          <MapPin className="h-4.5 w-4.5" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[12.5px] font-medium text-zinc-900">
            {body?.split(',')[0] ?? 'Shared location'}
          </span>
          <span className="block text-[11px] text-zinc-500">
            {coords ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)} · ` : ''}Open in Maps
          </span>
        </span>
      </a>
    );
  }

  if (!url || broken) {
    // Still resolving, or the media element failed - degrade to a link.
    return (
      <a
        href={url ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-xs underline ${url ? 'text-blue-600' : 'pointer-events-none text-zinc-400'}`}
      >
        {url ? 'View media' : 'Loading media…'}
      </a>
    );
  }

  if (kind === 'IMAGE') {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={url}
          alt={body ?? 'Image'}
          loading="lazy"
          onError={() => setBroken(true)}
          className="mb-1 max-h-56 w-full rounded-md object-cover"
        />
      </a>
    );
  }

  if (kind === 'VIDEO') {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        onError={() => setBroken(true)}
        className="mb-1 max-h-56 w-full rounded-md bg-black"
      />
    );
  }

  if (kind === 'AUDIO') {
    return (
      <audio
        src={url}
        controls
        preload="metadata"
        onError={() => setBroken(true)}
        className="mb-1 h-10 w-56 max-w-full"
      />
    );
  }

  // DOCUMENT (and anything else with a URL): WhatsApp-style file card.
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mb-1 flex items-center gap-2 rounded-md bg-black/5 p-2 no-underline hover:bg-black/10"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#128C7E] text-white">
        <FileText className="h-4.5 w-4.5" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[12.5px] font-medium text-zinc-900">
          {body ?? 'Document'}
        </span>
        <span className="block text-[11px] text-zinc-500">Tap to open</span>
      </span>
    </a>
  );
};

export default MediaBubble;
